import React, { useCallback, useMemo, useState } from 'react';
import { ERROR_MESSAGES, toUserMessage } from '../../utils/userErrors';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Clock3,
  Coins,
  Gift,
  Lock,
  Medal,
  Package,
  Tag,
  Trophy,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import apiService from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import { formatPts, formatPtsLabel, pick } from '../../utils/moduleHelpers';
import '../../styles/module-shared.css';
import './rewards-module.css';

const ProgressRing = ({ percent }) => {
  const p = Math.min(100, Math.max(0, Number(percent) || 0));
  const r = 18;
  const c = 2 * Math.PI * r;
  const offset = c - (p / 100) * c;
  return (
    <div className="rd-ring" aria-label={`${p}%`}>
      <svg viewBox="0 0 44 44">
        <circle cx="22" cy="22" r={r} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
        <circle
          cx="22"
          cy="22"
          r={r}
          fill="none"
          stroke="#5eead4"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="rd-ring__center">
        <strong>{p}%</strong>
        <span>Progress</span>
      </div>
    </div>
  );
};

const RewardsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);

  const fetchData = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      // Milestone progress is source of truth for track + stats.
      // AVAILABLE = AvailableLoyaltyPoints >= requiredPoints
      const [progressRes, dashRes, loyaltyRes] = await Promise.all([
        apiService.getMilestoneProgress(),
        apiService.getRewardsDashboard().catch(() => ({ success: false, data: null })),
        apiService.getLoyaltySummary().catch(() => ({ success: false, data: null })),
      ]);
      if (cancelled()) return;

      if (!progressRes.success && !dashRes.success) {
        setError(progressRes.error || dashRes.error || 'Failed to load rewards dashboard');
        setDashboard(null);
        return;
      }

      const progress = progressRes.success ? progressRes.data || {} : {};
      const dash = dashRes.success ? dashRes.data || {} : {};
      const loyalty = loyaltyRes?.success ? loyaltyRes.data || {} : {};

      const merged = {
        ...dash,
        ...progress,
        // Keep tier from full dashboard when milestone API doesn't include it.
        tierName: dash.tierName || dash.rewardTierName || progress.tierName || '—',
        rewardTierName: dash.rewardTierName || dash.tierName || progress.rewardTierName,
        rewardTierRequiredPoints:
          dash.rewardTierRequiredPoints ?? progress.rewardTierRequiredPoints ?? 0,
      };

      const loyaltyAvailable = Number(
        loyalty.availableLoyaltyPoints ?? loyalty.availablePoints ?? loyalty.loyaltyPoints
      );
      const loyaltyTotal = Number(
        loyalty.totalLoyaltyPoints ?? loyalty.totalPointsEarned
      );
      if (
        !(Number(merged.availablePoints) > 0) &&
        Number.isFinite(loyaltyAvailable) &&
        loyaltyAvailable > 0
      ) {
        merged.availablePoints = loyaltyAvailable;
        merged.availableLoyaltyPoints = loyaltyAvailable;
        merged.pointsToSpend = loyaltyAvailable;
      }
      if (
        !(Number(merged.totalPointsEarned) > 0) &&
        Number.isFinite(loyaltyTotal) &&
        loyaltyTotal > 0
      ) {
        merged.totalPointsEarned = loyaltyTotal;
        merged.totalLoyaltyPoints = loyaltyTotal;
      }

      // AVAILABLE logic: AvailableLoyaltyPoints >= required
      const available = Number(
        merged.availablePoints ?? merged.availableLoyaltyPoints ?? 0
      );
      const target = Number(merged.nextRewardTarget ?? 0);
      if (target > 0) {
        merged.pointsNeeded = Math.max(0, target - available);
        merged.nextRewardCurrent = Math.min(available, target);
        merged.nextRewardProgress = Math.min(
          100,
          Math.max(0, Math.round((available / target) * 100))
        );
      }

      setDashboard(merged);
    } catch (err) {
      if (!cancelled()) setError(toUserMessage(err, ERROR_MESSAGES.load));
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey('rewards-dashboard-milestone-v1', fetchData, true);

  const availablePoints = Number(
    dashboard?.availablePoints ?? dashboard?.availableLoyaltyPoints ?? dashboard?.pointsToSpend ?? 0
  );
  const totalPointsEarned = Number(
    dashboard?.totalPointsEarned ?? dashboard?.totalLoyaltyPoints ?? 0
  );
  const tierName = pick(dashboard || {}, 'tierName', 'rewardTierName') || '—';
  const tierRequired = Number(dashboard?.rewardTierRequiredPoints ?? 0);

  const nextRewardName = dashboard?.nextRewardName || 'Next milestone';
  const pointsNeeded = Number(dashboard?.pointsNeeded ?? 0);
  const nextRewardProgress = Number(dashboard?.nextRewardProgress ?? 0);
  const nextTarget = Number(dashboard?.nextRewardTarget ?? 0);

  const milestones = useMemo(() => dashboard?.milestones || [], [dashboard]);

  // Claimed only — e.g. 2 claimed out of 15 → "2 / 15"
  const milestonesReached = milestones.filter(
    (m) => m.state === 'claimed' || m.isClaimed === true || String(m.badge || '').toLowerCase() === 'claimed'
  ).length;
  const totalMilestones = Number(dashboard?.totalMilestones ?? milestones.length) || milestones.length;
  const rewardsAvailable = Number(dashboard?.rewardsAvailable ?? 0);
  const pendingCount = Number(dashboard?.pendingCount ?? dashboard?.pendingRewards ?? 0);
  const claimedCount = Number(dashboard?.claimedCount ?? milestonesReached);
  const totalValueUnlocked = Number(dashboard?.totalValueUnlocked ?? 0);
  const totalValueLabel = `$${Number(totalValueUnlocked).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  const fillWidth = totalMilestones
    ? Math.min(100, Math.round((milestonesReached / Math.max(totalMilestones, 1)) * 100))
    : 0;

  const stats = [
    {
      label: 'Milestones Reached',
      value: `${milestonesReached} / ${totalMilestones || 0}`,
      icon: Trophy,
      bg: '#ecfdf5',
      color: '#059669',
    },
    {
      label: 'Rewards Available',
      value: formatPts(rewardsAvailable),
      icon: Tag,
      bg: '#ecfdf5',
      color: '#8b0000',
    },
    {
      label: 'Pending Rewards',
      value: formatPts(pendingCount),
      icon: Clock3,
      bg: '#fff7ed',
      color: '#ea580c',
    },
    {
      label: 'Total Value Unlocked',
      value: totalValueLabel,
      icon: Coins,
      bg: '#eff6ff',
      color: '#2563eb',
    },
  ];

  const actions = [
    {
      count: formatPts(rewardsAvailable),
      label: 'Rewards Available',
      cta: 'Browse Rewards',
      to: '/rewards/claim',
      tone: 'green',
    },
    {
      count: formatPts(pendingCount),
      label: 'Pending Rewards',
      cta: 'View Pending',
      to: '/rewards/report',
      tone: 'orange',
    },
    {
      count: formatPts(claimedCount),
      label: 'Rewards Claimed',
      cta: 'View History',
      to: '/rewards/report',
      tone: 'blue',
    },
    {
      count: totalValueLabel,
      label: 'Total Value Unlocked',
      cta: 'View Details',
      to: '/rewards/report',
      tone: 'pink',
    },
  ];

  const nextRewardCopy =
    nextTarget > 0
      ? pointsNeeded > 0
        ? `${formatPts(pointsNeeded)} PTS needed to unlock ${nextRewardName}`
        : `Ready to claim ${nextRewardName}`
      : nextRewardName;

  return (
    <div className="page rd-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={Gift}
          title="Rewards"
          subtitle="Earn points, unlock rewards, and enjoy exclusive benefits."
          iconBg="linear-gradient(135deg,#6b0000,#8b0000)"
        />

        <ModuleAlert message={error} />

        {loading ? (
          <div className="module-card">
            <AppLoader color="#8b0000" />
          </div>
        ) : (
          <>
            <section className="rd-hero">
              <div className="rd-hero__block">
                <div className="rd-hero__label">Available Points</div>
                <div className="rd-hero__value pts">
                  {formatPts(availablePoints)}
                  <span className="pts-unit">PTS</span>
                </div>
              </div>

              <div className="rd-hero__block">
                <div className="rd-hero__label">Your Tier</div>
                <div className="rd-hero__tier">
                  <Medal className="w-7 h-7 text-amber-200" />
                  <div>
                    <div className="rd-hero__value" style={{ fontSize: '1.25rem' }}>
                      {tierName}
                    </div>
                    <div className="rd-hero__meta">
                      {tierRequired > 0
                        ? `${formatPtsLabel(tierRequired)} threshold`
                        : 'No tier threshold'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rd-hero__block">
                <div className="rd-hero__label">Next Reward</div>
                <div className="rd-hero__next">
                  <div className="rd-hero__next-copy">{nextRewardCopy}</div>
                  <ProgressRing percent={nextRewardProgress} />
                </div>
              </div>

              <div className="rd-hero__block">
                <div className="rd-hero__label">Total Points Earned</div>
                <div className="rd-hero__value pts">{formatPts(totalPointsEarned)}</div>
                <Link className="rd-hero__btn" to="/rewards/report">
                  View History <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </section>

            <section className="rd-stats">
              {stats.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="module-card rd-stat">
                    <div className="rd-stat__chip" style={{ background: s.bg, color: s.color }}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="rd-stat__value pts">{s.value}</div>
                      <div className="rd-stat__label">{s.label}</div>
                    </div>
                  </div>
                );
              })}
            </section>

            <section className="module-card rd-milestones">
              <h2 className="rd-milestones__title">Milestone Progress</h2>
              {milestones.length === 0 ? (
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  No milestones available yet.
                </p>
              ) : (
                <div className="rd-track">
                  <div className="rd-track__fill" style={{ width: `${fillWidth}%` }} />
                  {milestones.map((m) => {
                    let state = String(m.state || '').toLowerCase();
                    if (state === 'completed' || state === 'claimed') state = 'claimed';
                    else if (state === 'pending') state = 'pending';
                    else if (state === 'available' || state === 'current') state = 'current';
                    else state = 'locked';

                    return (
                      <div key={m.id} className={`rd-step is-${state}`}>
                        <div className="rd-step__icon">
                          {state === 'claimed' ? (
                            <Trophy className="w-5 h-5" />
                          ) : state === 'pending' || state === 'current' ? (
                            <Package className="w-5 h-5" />
                          ) : (
                            <Lock className="w-4 h-4" />
                          )}
                        </div>
                        <div className="rd-step__pts pts">
                          {formatPts(m.requiredPoints || m.points)} PTS
                        </div>
                        <div className="rd-step__badge">{m.badge || m.status || state}</div>
                        <div className="rd-step__name">{m.name}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="rd-actions">
              {actions.map((a) => (
                <Link key={a.label} to={a.to} className={`rd-action rd-action--${a.tone}`}>
                  <div className="rd-action__count pts">{a.count}</div>
                  <div className="rd-action__label">{a.label}</div>
                  <div className="rd-action__cta">
                    {a.cta} <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </Link>
              ))}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default RewardsDashboard;
